import * as babelParser from '@babel/parser';
import {
    highlightedFunctionNames,
    highlightedObjectNames,
} from '../const/keywords';
import * as PegParser from '../services/PegParser';
import * as PegPlayParser from '../services/PegPlayParser';
import { JSHINT } from 'jshint';


let actionUId = 0;
let contextIndex = 1;
const maxSearch = 10;

const jshintOptions = {
    esversion: 11,
    undef: true,
    asi: true,
    node: true,
};

let jshintPredefineOptions = [
    ...highlightedFunctionNames,
    ...highlightedObjectNames
].reduce((memo, el) => {
    memo[el] = false;
    return memo;
}, {});

jshintPredefineOptions = {
    ...jshintPredefineOptions,
    "setLanguages": false,
    "ru": false,
    "es": false,
    "it": false,
    "de": false,
    "fr": false,
    "en": false
};

function validateWithJsHint(script) {
    JSHINT(script, jshintOptions, jshintPredefineOptions);
    return JSHINT.data().errors?.filter(err => (err.raw.indexOf('Unnecessary semicolon') === -1) && err.raw.indexOf('Functions declared within loops referencing an outer scoped variable may lead to confusing semantics') === -1) || [];
}

function canBeCompiledInPreview(obj) {
    let result = true;

    const checkProp = (curProp) => {
        if (curProp.type === 'StringLiteral'
            || curProp.type === 'NumericLiteral'
            || curProp.type === 'BooleanLiteral') {
            return true;
        }
        return false;
    };

    if (obj.value.properties || obj.value.elements) {
        const items = obj.value.properties || obj.value.elements;
        for (var prop in items) {
            const curProp = items[prop];
            if (curProp.type === 'ObjectProperty'
                || curProp.type === 'ArrayExpression') {
                result = canBeCompiledInPreview(curProp);
                if (!result) {
                    break;
                }
            } else {
                result = checkProp(curProp);
                break;
            }
        }
    } else {
        result = checkProp(obj.value);
    }

    return result;
}

export class Context {
    constructor(id, index, line) {
        this.id = id;
        this.index = index;
        this.line = line && line.start.line;
        this.actions = [];
        this.title = null;
        this.hasRelations = false;
        this.label = this.id === 'root' ? '' : this.id;
    }
}

export class VisualRelation {
    constructor(name, target) {
        this.name = name;
        this.target = target;
    }
}

export class VisualFilter {
    constructor(name, line, endLine) {
        this.id = ++actionUId;
        this.name = name;
        this.line = line;
        this.endLine = endLine;
    }
}

class ProjectVariable {
    constructor(name, type) {
        this.name = name;
        this.type = type;
    }
}

export class Action {
    constructor(type, loc, contextId, valueText) {
        this.type = type;
        this.line = loc.start.line;
        this.endLine = loc.end.line;
        this.loc = loc;
        this.valueText = valueText;
        this.contextId = contextId;
        this.id = ++actionUId;
        this.hasRelations = false;
        this.error = false;
        this.hasVisualFilter = false;
        this.resolve = false;
    }
}

export class ContextParser {

    constructor() {
        this.reset();
    }

    reset() {
        this.context = [];
        this.relationships = [];
        this.imports = new Map();
        this.methodsMap = {};
        this.variableMap = {};
        this.visualMap = {};
        this.visualRelationships = [];
        this.visualRelationshipsMap = {};
        this.projectVariableRelationships = [];
        this.projectVariableMap = {};
        this.projectVariablesList = [];
        this.membersMap = {};
        this.declarationIndex = new Map();
        this.visualList = [];
        this.comments = new Map();
        this.strings = [];
        this.brackets = {};
        this.bracketsInStrings = {};
        this.invalidBrackets = [];
        this.syntaxErrors = [];
        this.patternStrings = new Set();
        this.playStrings = new Set();
        this.script = '';
        this.alanBtnPopups = {};
        this.projectApis = {};
        this.intents = {};
        this.transforms = {};
        this.corpuses = {};
        this.fileName = '';
    }

    nodeIsContext(node) {
        if (node.type === 'VariableDeclaration') {
            return node.declarations.some(declaration => {
                return declaration.init?.callee && declaration.init.callee.name === 'context';
            });
        }
        return node.type === 'ExpressionStatement' && node.expression.type === 'CallExpression' && node.expression.callee.name === 'context';
    }

    nodeIsVisual(node) {
        if (node.type === 'VariableDeclaration') {
            return node.declarations.some(declaration => {
                return declaration.init?.callee && declaration.init.callee.name === 'visual';
            });
        }
        return node.type === 'ExpressionStatement' && node.expression.type === 'CallExpression' && node.expression.callee.name === 'visual';
    }

    nodeIsIntent(node) {
        return node.type === 'ExpressionStatement' && node.expression.type === 'CallExpression' && node.expression.callee.name === 'intent';
    }

    nodeIsFollow(node) {
        return node.type === 'ExpressionStatement' && node.expression.type === 'CallExpression' && node.expression.callee.name === 'follow';
    }

    nodeIsQuestion(node) {
        return node.type === 'ExpressionStatement' && node.expression.type === 'CallExpression' && node.expression.callee.name === 'question';
    }

    nodeIsFallback(node) {
        return node.type === 'ExpressionStatement' && node.expression.type === 'CallExpression' && node.expression.callee.name === 'fallback';
    }

    nodeIsTitle(node) {
        return node.type === 'ExpressionStatement' && node.expression.type === 'CallExpression' && node.expression.callee.name === 'title';
    }

    compileTemplateLiteral(node) {
        let resultString = '';
        node.quasis.forEach((quasis, index) => {
            resultString += quasis.value.raw;
            const expression = node.expressions[index];
            if (expression) {
                const results = [];
                this.parseTemplateString(results, expression);
                this.addStringBracketPair(
                    expression.loc.start.line,
                    expression.loc.start.column - 1,
                    expression.loc.end.line,
                    expression.loc.end.column);
                resultString += results[0] || '${' + (expression.name || expression.value) + '}';
            }
        });
        return resultString;
    }

    evalBinaryTemplate(node) {
        let result = '';
        if (node.type === 'BinaryExpression') {
            result += this.evalBinaryTemplate(node.left);
            result += this.evalBinaryTemplate(node.right, result);
        } else if (node.type === 'TemplateLiteral') {
            result += this.compileTemplateLiteral(node);
        } else if (node.type === 'StringLiteral') {
            result += node.value;
        } else if (node.type === 'Identifier') {
            const variableNode = this.variableMap[node.name];
            if (variableNode) {
                result += this.evalBinaryTemplate(variableNode);
            }
        }
        return result;
    }

    parseTemplate(action, text) {
        let prev = 0;
        if (!text) {
            return [];
        }
        const textNodes = [...text.matchAll(
            /\$\(([a-zA-Z0-9_*~.+\s]+)/gm
        )].reduce((results, searchIterator) => {
            results.push({
                type: 'text',
                value: text.slice(prev, searchIterator.index),
            });
            const searchStart = searchIterator.index + searchIterator[0].length;
            const bracketStack = [];
            let title = '';
            prev = searchStart + 1 + [...text.slice(searchStart)].findIndex(letter => {
                if (letter === ')') {
                    if (bracketStack.length) {
                        bracketStack.pop();
                    } else {
                        return true;
                    }
                } else if (letter === '(') {
                    bracketStack.push('(');
                }
                title += letter;
                return false;
            });
            let value = searchIterator[1] || searchIterator[2];
            title = (title || value).trim();

            const projectVariableSearch = title.match(/([puv]):([a-zA-Z0-9_]+)/);

            if (projectVariableSearch) {
                if (!this.projectVariableMap[projectVariableSearch[0]]) {
                    this.projectVariableMap[projectVariableSearch[0]] = new ProjectVariable(projectVariableSearch[2], projectVariableSearch[1]);
                }

                this.projectVariableRelationships.push({
                    target: action,
                    source: this.projectVariableMap[projectVariableSearch[0]],
                });
            }

            if (value === '~' && projectVariableSearch) {
                value = projectVariableSearch[2];
            }

            results.push({
                type: 'variable',
                value,
                title,
            });
            return results;
        }, []);
        const tail = text.slice(prev);
        if (tail) {
            textNodes.push({
                type: 'text',
                value: tail,
            });
        }
        return textNodes;
    }

    processReplyRoutine(node, visualRelations, parentAction, parentContext) {
        const args = node.arguments || node.expression.arguments;
        const last = args[args.length - 1];

        if (last && last.type === 'CallExpression' && last.callee.name === 'reply') {
            this.processReplyRoutine(last, visualRelations, parentAction, parentContext);
        } else if (last && last.type === 'CallExpression' && last.callee.name === 'follow') {
            const value = this.getTemplateStringValue(last);
            const id = ++contextIndex;
            const context = new Context(id, id, last.loc);
            const action = new Action('follow', last.loc, context.id, value.join('\n'));
            parentAction.hasRelations = true;
            this.relationships.push({
                action: parentAction,
                id,
                parentContext,
            });
            action.values = value.map(this.parseTemplate.bind(this, action));
            context.actions.push(action);
            this.context.push(context);
            this.processReplyRoutine(last, visualRelations, action, context);
        }
    }

    getTemplateStringValue(node, visualRelations) {
        const args = node.arguments || node.expression.arguments;
        return args.slice(0, args.length)
            .reduce(this.parseTemplateString.bind(this), []);
    }

    parseTemplateString(results, template) {
        let resultsData = results;
        if (template.type === 'TemplateLiteral') {
            resultsData.push(this.compileTemplateLiteral(template));
            template.expressions.forEach(node => {
                this.parseTemplateString([], node);
            });
            if (this.isPlay) {
                this.playStrings.add(template);
            } else {
                this.patternStrings.add(template);
            }
        } else if (template.type === 'StringLiteral') {
            resultsData.push(template.value);
            if (this.isPlay) {
                this.playStrings.add(template);
            } else {
                this.patternStrings.add(template);
            }
        } else if (template.type === 'BinaryExpression') {
            resultsData.push(this.evalBinaryTemplate(template));
        } else if (template.type === 'Identifier') {
            const variableNode = this.declarationIndex.get(`${template.name}.${template.loc.start.line}`)?.node;
            const visualRelation = this.visualMap[template.name];
            if (visualRelation && this.currentVisualRelations) {
                this.currentVisualRelations.push(template.name);
            } else if (variableNode) {
                this.parseTemplateString(resultsData, variableNode);
            }
        } else if (template.type === 'ArrayExpression') {
            template.elements.forEach(el => {
                this.parseTemplateString(resultsData, el);
            });
        } else if (template.type === 'SpreadElement') {
            return this.parseTemplateString(resultsData, template.argument);
        }
        return resultsData;
    }

    findDeclarations(node, state) {
        state = state || {};
        if (node.type === 'ArrowFunctionExpression' || node.type === 'FunctionExpression') {
            state = { ...state };
        } else if (node.type === 'VariableDeclarator') {
            if (node.id.type === 'ObjectPattern') {
                node.id.properties.forEach(_ => {
                    state[_.key.name] = { id: _.key, node: _.init };
                });
            } else {
                state[node.id.name] = { id: node.id, node: node.init };
            }
        } else if (node.type === 'FunctionDeclaration') {
            state = { ...state };
            state[node.id.name] = { id: node.id, node };
            node.params.forEach(param => {
                state[param.name] = { id: param };
            });
            this.methodsMap[node.id.name] = { ...node, fileName: this.fileName };
        } else if (node.type === 'ImportDeclaration') {
            node.specifiers.forEach(specifier => {
                this.imports.set(`${specifier.imported.name}.${node.source.value}`, { ...specifier, source: node.source.value });
            });

        } else if (node.type === 'Identifier') {
            if (Object.prototype.hasOwnProperty.call(state, node.name)) {
                this.declarationIndex.set(`${node.name}.${node.loc.start.line}`, {
                    name: node.name,
                    loc: state[node.name].id.loc,
                    node: state[node.name].node,
                });
            } else if (this.methodsMap[node.name] || this.variableMap[node.name]) {
                const declareNode = this.methodsMap[node.name]
                    ? this.methodsMap[node.name].id || this.methodsMap[node.name].init
                    : this.variableMap[node.name];
                const loc = declareNode ? declareNode.loc : this.variableMap[node.name].loc;
                this.declarationIndex.set(`${node.name}.${node.loc.start.line}`, {
                    name: node.name,
                    loc,
                    node: declareNode,
                });
            }
        }
        return state;
    }

    findPatternStrings(node, state, check) {
        if (node.type === 'CallExpression') {
            if (
                (node.callee?.object?.name === 'p' && node.callee?.property?.name === 'play')
                || node.callee?.name === 'reply'
                || node.callee?.name === 'fallback'
            ) {
                this.isPlay = true;
                this.getTemplateStringValue(node);
                this.isPlay = false;
            } else if (
                node.callee?.name === 'intent'
                || node.callee?.name === 'follow'
                || node.callee?.name === 'question'
            ) {
                this.collectIntents(node);
                this.getTemplateStringValue(node);
            }
        } else if (node.type === 'TemplateLiteral' || node.type === 'StringLiteral') {
            if (!this.playStrings.has(node) && !this.patternStrings.has(node)) {
                this.strings.push(node);
            }
        }
        return state;
    }

    parseFlowDiagramData(node, state, check) {
        if (!state) {
            const context = new Context('root', 1, {
                start: {
                    line: 1,
                },
            });
            this.context.push(context);
            state = {
                context,
                action: null,
                visualRelations: [],
            };
        }

        let actionName;

        if (this.nodeIsIntent(node)) {
            actionName = 'intent';
        } else if (this.nodeIsFollow(node)) {
            actionName = 'follow';
        } else if (this.nodeIsQuestion(node)) {
            actionName = 'question';
        } else if (this.nodeIsFallback(node)) {
            actionName = 'fallback';
        }
        if (actionName) {
            state = { ...state, visualRelations: [...state.visualRelations] };
            const { visualRelations } = state;
            this.currentVisualRelations = visualRelations;
            let value = this.getTemplateStringValue(node);
            const action = new Action(actionName, node.loc, state.context.id, value.join('\n'));
            state.action = action;
            action.values = value.map(this.parseTemplate.bind(this, action));
            visualRelations.forEach(name => {
                if (!this.visualRelationshipsMap[`${name}.${action.id}`]) {
                    this.visualRelationshipsMap[`${name}.${action.id}`] = new VisualRelation(name, action);
                }
            });
            action.hasVisualFilter = !!visualRelations.length;
            state.context.actions.push(action);
            this.processReplyRoutine(node, visualRelations, action, state.context);
        } else if (this.nodeIsContext(node)) {
            if (node.type === 'VariableDeclaration') {
                node.declarations.forEach(declaration => {
                    if (declaration.init.callee && declaration.init.callee.name === 'context' && declaration.init.arguments?.length) {
                        state = { ...state };
                        state.context = new Context(declaration.id.name, ++contextIndex, node.loc);
                    }
                });
            } else if (node.expression.arguments?.length) {
                state = { ...state };
                state.context = new Context(node.loc.start.line, ++contextIndex, node.loc);
            }
            this.context.push(state.context);
        } else if (this.nodeIsTitle(node)) {
            state.context.title = node.expression.arguments[0].value;
        } else if (this.nodeIsVisual(node)) {
            if (node.type === 'VariableDeclaration') {
                node.declarations.forEach(declaration => {
                    if (declaration.init.callee && declaration.init.callee.name === 'visual') {
                        this.visualMap[declaration.id.name] = new VisualFilter(declaration.id.name, node.loc.start.line, node.loc.end.line);
                    }
                });
            }
        } else if (state.action && state.action.type === 'fallback' && node.type === 'CallExpression' && node.callee.object && node.callee.object.name === state.pName && (!state.action?.values || !state.action?.valueText)) {
            if (node.callee.property.name === 'play') {
                this.currentVisualRelations = null;
                const value = this.getTemplateStringValue(node);
                if (value) {
                    state.action.values = value.map(this.parseTemplate.bind(this, state.action));
                    state.action.valueText = value.join('\n');
                }
            }
        } else if (state.action && state.action.type === 'fallback' && !state.action.valueText) {
            const { action } = state;
            const args = node.arguments || node.expression.arguments;
            const last = args[args.length - 1];
            let value = '';
            if (last && last.type === 'CallExpression' && last.callee.name === 'reply') {
                this.currentVisualRelations = state.visualRelations;
                value = this.getTemplateStringValue(last);
            } else {
                const fallbackCount = state.context.actions.reduce((count, action) => {
                    if (action.type === 'fallback') {
                        count++;
                    }
                    return count;
                }, 0);
                value = [`Fallback ${fallbackCount + 1}`];
            }
            action.values = value.map(this.parseTemplate.bind(this, action));
            action.valueText = value.join('\n');
        } else if (node.type === 'CallExpression' && node.callee.object && node.callee.object.name === state.pName && state.action) {
            if (node.callee.property.name === 'then') {
                state.action.hasRelations = true;
                this.relationships.push({
                    action: state.action,
                    id: node.arguments[0].name,
                    parentContext: state.context,
                });
            } else if (node.callee.property.name === 'resolve') {
                state.action.resolve = true;
            }
        }

        if (node.type === 'ArrowFunctionExpression') {
            state.pName = node.params[0]?.name;
        }

        return state;
    }

    parseScript(nodes, onlyErrors) {
        const callbacks = [
            this.findDeclarations.bind(this),
            this.parseFlowDiagramData.bind(this),
            this.findPatternStrings.bind(this),
            this.parseMemberExpressionHighlights.bind(this),
            this.parseBrackets.bind(this),
            this.parseAlanBtnPopus.bind(this),
            this.parseProjectApis.bind(this),
            this.parseTransforms.bind(this),
            this.parseCorpuses.bind(this),
        ];

        this.findThen(nodes, [null], [this.findDeclarations.bind(this)]);
        this.findThen(nodes, onlyErrors ? [{}] : [null, null, {}, null, {}, {}, {}, {}, {}], onlyErrors ? [callbacks[2]] : callbacks);
    }

    parseBrackets(node, state) {
        // console.info('== parseBrackets', this.script.slice(node.start, node.end), node, node.type, node.name);

        if (
            node.type === 'BlockStatement'
            || node.type === 'ObjectExpression'
            || node.type === 'ArrayExpression'
            || node.type === 'ParenthesizedExpression'
            || node.type === 'ArrayPattern'
            || node.type === 'ObjectPattern'
        ) {
            this.addBracketPair(
                node.loc.start.line,
                node.loc.start.column,
                node.loc.end.line,
                node.loc.end.column - 1,
            );
        } else if (node.type === 'CallExpression') {
            const id = this.getCallId(node) || node;

            let index = id.start + id.name.length;
            let offset = 0;

            while (this.script[index] !== '(' && offset < maxSearch) {
                index++;
                offset++;
            }

            const startOffset = offset;

            this.addBracketPair(
                id.loc.start.line,
                id.loc.start.column + id.name.length + startOffset,
                node.loc.end.line,
                node.loc.end.column - 1,
            );
        } else if (node.type === 'NewExpression') {
            let index = node.end;
            let offset = 0;

            while (this.script[index] !== '(' && offset < maxSearch) {
                index--;
                offset++;
            }

            const startOffset = offset;

            this.addBracketPair(
                node.loc.start.line,
                node.loc.end.column - startOffset,
                node.loc.end.line,
                node.loc.end.column - 1,
            );
        } else if (node.type === 'RegExpLiteral') {
            let regexBrackets = {
                '[': ']',
                '(': ')',
                '{': '}'
            };
            const stack = [];
            const { pattern } = node;
            const globalStart = node.loc.start.column;

            for (let i = 0; i < pattern.length; i++) {
                let char = pattern[i];

                if (char in regexBrackets) {
                    stack.push(char, i);
                }
                else if (char === regexBrackets[stack[stack.length - 2]]) {
                    this.addBracketPair(
                        node.loc.start.line,
                        globalStart + stack.pop() + 1,
                        node.loc.start.line,
                        globalStart + i + 1
                    );
                    stack.pop();
                }
            }
        } else if (node.type === 'IfStatement' || node.type === 'SwitchStatement') {
            const test = node.test || node.discriminant;

            let endIndex = test.end;
            let endOffset = 0;

            while (this.script[endIndex] !== ')' && endOffset < maxSearch) {
                endIndex++;
                endOffset++;
            }

            let index = node.start;
            let offset = 0;

            while (this.script[index] !== '(' && offset < maxSearch) {
                index++;
                offset++;
            }

            this.addBracketPair(
                node.loc.start.line,
                node.loc.start.column + offset,
                test.loc.end.line,
                test.loc.end.column + endOffset,
            );
        } else if (node.type === 'FunctionExpression' || node.type === 'FunctionDeclaration' || node.type === 'ArrowFunctionExpression') {
            const body = node.body;

            if (node.type === 'ArrowFunctionExpression' || node.type === 'FunctionExpression') {
                if (node.body.body?.length === 1) {
                    if (this.script[node.start - 1] === '(' && this.script[node.end] === ')') {
                        this.addBracketPair(
                            node.loc.start.line,
                            node.loc.start.column - 1,
                            node.loc.end.line,
                            node.loc.end.column,
                        );
                    }
                }
            }

            if (node.type === 'ArrowFunctionExpression' && this.script[node.start] !== '(') {
                return state;
            }

            let endIndex = body.start;
            let endOffset = 0;

            while (this.script[endIndex] !== ')' && endIndex >= 0) {
                endIndex--;
                endOffset--;
            }

            let index = node.start;
            let offset = 0;

            while (this.script[index] !== '(' && this.script[index] !== undefined) {
                index++;
                offset++;
            }

            this.addBracketPair(
                node.loc.start.line,
                node.loc.start.column + offset,
                body.loc.start.line,
                body.loc.start.column + endOffset,
            );
        } else if (node.type === 'MemberExpression' && node.computed) {
            if (node.object.type === 'MemberExpression') {
                let index = node.end;
                let offset = 0;

                while (this.script[index] !== '[' || node.end - offset < 0) {
                    index--;
                    offset++;
                }

                this.addBracketPair(
                    node.loc.start.line,
                    node.loc.end.column - offset,
                    node.loc.end.line,
                    node.loc.end.column - 1,
                );
            } else if (node.object.type === 'CallExpression' || node.object.type === 'Identifier') {
                const id = this.getCallId(node.object);
                const name = id.name;

                let index = id.start + name.length;
                let offset = 0;

                while (this.script[index] !== '[' && offset < maxSearch) {
                    index++;
                    offset++;
                }

                this.addBracketPair(
                    id.loc.start.line,
                    id.loc.start.column + name.length + offset,
                    node.loc.end.line,
                    node.loc.end.column - 1,
                );
            }
        } else if (node.type === 'TemplateLiteral') {
            if (node.expressions) {
                node.expressions.forEach(exp => {
                    if (this.script.slice(exp.start - 2, exp.start) === '${' && this.script[exp.end] === '}') {
                        this.addStringBracketPair(
                            exp.loc.start.line,
                            exp.loc.start.column - 1,
                            exp.loc.end.line,
                            exp.loc.end.column
                        );
                    }
                });
            }
        }

        return state;
    }

    parseAlanBtnPopus(node, state) {
        if (node.type === 'ObjectExpression') {

            const htmlProp = node.properties?.find(prop => prop.key.name.toLowerCase() === 'html');
            const styleProp = node.properties?.find(prop => prop.key.name.toLowerCase() === 'style');
            const nameProp = node.properties?.find(prop => prop.key.name.toLowerCase() === 'name');
            const buttonMarginInPopupProp = node.properties?.find(prop => prop.key.name.toLowerCase() === 'buttonmargininpopup');
            const type = node.properties?.find(prop => prop.key.name.toLowerCase() === 'type');
            const paramsProp = node.properties?.find(prop => prop.key.name.toLowerCase() === 'params');

            if (htmlProp && styleProp) {
                const startLine = node.loc.start.line;
                const startCol = node.loc.start.column;
                const endLine = node.loc.end.line;
                const endCol = node.loc.end.column;

                const startLoc = `${startLine}.${startCol}`;
                const endLoc = `${endLine}.${endCol}`;

                const getStringPropValue = (propNode) => {
                    if (propNode.value.type === 'StringLiteral') {
                        return propNode.value.value;
                    }
                    if (propNode.value.type === 'TemplateLiteral') {
                        return this.script.slice(propNode.value.start + 1, propNode.value.end - 1);
                    }
                    if (propNode.value.type === 'NumericLiteral') {
                        return propNode.value.value;
                    }
                };
                const propPositions = {
                    htmlPosition: {
                        start: htmlProp.value.loc.start,
                        end: htmlProp.value.loc.end,
                        startChar: htmlProp.value.start + 1,
                        endChar: htmlProp.value.end - 1
                    },
                    stylePosition: {
                        start: styleProp.value.loc.start,
                        end: styleProp.value.loc.end,
                        startChar: styleProp.value.start + 1,
                        endChar: styleProp.value.end - 1
                    },
                };
                let paramsCanBeCompiledInPreview = false;

                if (paramsProp) {
                    propPositions.paramsPosition = {
                        start: paramsProp ? paramsProp.value.loc.start : null,
                        end: paramsProp ? paramsProp.value.loc.end : null,
                        startChar: paramsProp ? paramsProp.value.start : null,
                        endChar: paramsProp ? paramsProp.value.end : null
                    };
                    paramsCanBeCompiledInPreview = canBeCompiledInPreview(paramsProp);
                }

                this.alanBtnPopups[startLoc + ':' + endLoc] = {
                    name: nameProp ? (getStringPropValue(nameProp) || null) : null,
                    html: getStringPropValue(htmlProp) || '',
                    style: getStringPropValue(styleProp) || '',
                    buttonMarginInPopup: buttonMarginInPopupProp ? (getStringPropValue(buttonMarginInPopupProp) || null) : null,
                    type: type ? (getStringPropValue(type) || null) : null,
                    params: paramsProp ? this.script.slice(paramsProp.value.start, paramsProp.value.end) : null,
                    paramsCanBeCompiledInPreview,
                    togglerPosition: {
                        line: node.loc.start.line,
                        column: node.loc.start.column,
                    },
                    ...propPositions
                };
            }
        }
        return state;
    }

    parseProjectApis(node, state) {
        const isExportDecalaration = node.type === 'ExportNamedDeclaration' || node.type === 'ExportDefaultDeclaration';
        if (node.type === 'MemberExpression') {
            if (node?.object?.name === 'projectAPI') {
                this.projectApis[`${node.loc.start.line}`] = {
                    name: node?.property?.name,
                    loc: node.loc,
                    type: 'projectAPI'
                };
            }
        } else if (node.type === 'FunctionDeclaration' ||
            (isExportDecalaration && node.declaration?.type === 'FunctionDeclaration')
        ) {
            let funcName = isExportDecalaration ? node.declaration?.id?.name : node?.id?.name;
            if (funcName) {
                this.projectApis[`${node.loc.start.line}`] = {
                    name: funcName,
                    loc: node.loc,
                    type: 'function'
                };
            }
        }
        return state;
    }

    parseTransforms(node, state) {
        if (node.type === 'Identifier') {
            if (node.name === "transforms") {
                this.transforms[`${node.loc.start.line}`] = {
                    loc: node.loc
                };
            } else if (this.transforms[`${node.loc.start.line}`] && !this.transforms[`${node.loc.start.line}`].name) {
                this.transforms[`${node.loc.start.line}`] = {
                    ...this.transforms[`${node.loc.start.line}`],
                    name: node.name
                };
            }
        }
        return state;
    }

    parseCorpuses(node, state) {
        const regex = /^corpus(_v\d+)?$/;
        if (node.type === 'Identifier' && regex.test(node.name)) {
            this.corpuses[`${node.loc.start.line}`] = {
                name: node.name,
                loc: node.loc
            };
        }
        return state;
    }


    collectIntents(node, state) {
        if (node.callee?.name === 'intent') {
            let intentValue = '';

            if (node?.arguments[0] && node?.arguments[0].value) {
                intentValue = node?.arguments[0].value;
            } else if (node?.arguments[0] && node?.arguments[0].quasis && node?.arguments[0].quasis[0]) {
                intentValue = node?.arguments[0].quasis[0].value?.raw;
            }

            this.intents[`${node.loc.start.line}`] = {
                value: intentValue || '',
                loc: node.loc
            };
        }
    }

    getCallId(node) {
        if (node.type === 'MemberExpression' || node.type === 'OptionalMemberExpression') {
            return this.getCallId(node.property);
        } else if (node.type === 'CallExpression') {
            return this.getCallId(node.callee);
        } else if (node.type === 'Identifier') {
            return node;
        }
    }

    getName(node, innerState = { prefix: '' }) {
        if (node.type === 'MemberExpression' || node.type === 'OptionalMemberExpression') {
            this.getName(node.object, innerState);
            const name = `${innerState.prefix}${node.property.name}`;
            innerState.prefix += `${node.property.name}.`;
            innerState.name = name;
        } else {
            const name = `${innerState.prefix}${node.name}`;
            innerState.name = name;
        }
        return innerState.name;
    }

    addBracketPair(startLine, startCol, endLine, endCol) {
        const startLoc = `${startLine}.${startCol}`;
        const endLoc = `${endLine}.${endCol}`;
        this.brackets[startLoc] = endLoc;
        this.brackets[endLoc] = startLoc;
    }


    addStringBracketPair(startLine, startCol, endLine, endCol) {
        const startLoc = `${startLine}.${startCol}`;
        const endLoc = `${endLine}.${endCol}`;
        this.bracketsInStrings[startLoc] = endLoc;
        this.bracketsInStrings[endLoc] = startLoc;
    }

    parseMemberExpressionHighlights(node, state, check) {
        state = state || {};
        if (node.type === 'FunctionDeclaration' || node.type === 'ArrowFunctionExpression' || node.type === 'FunctionExpression') {
            if (node.type === 'FunctionDeclaration') {
                this.addMemberExpression(node.id.name, state, node.id);
            }
            state = { ...state };
        } else if (node.type === 'Identifier') {
            this.addMemberExpression(node.name, state, node);
        } else if (node.type === 'MemberExpression' || node.type === 'OptionalMemberExpression') {
            this.parseMemberExpression(node, state);
        }
        return state;
    }

    parseMemberExpression(node, state, innerState = {}) {
        if (node.type === 'MemberExpression' || node.type === 'OptionalMemberExpression') {
            this.parseMemberExpression(node.object, state, innerState);
            const name = `${innerState.prefix}${node.property.name}`;
            innerState.prefix += `${node.property.name}.`;

            this.addMemberExpression(name, state, node.property);
        } else if (node.type === 'CallExpression') {
            this.parseMemberExpression(node.callee, state, innerState);
            if (node.arguments) {
                node.arguments.forEach(arg => {
                    this.parseMemberExpression(arg, state, { prefix: '' });
                });
            }
        } else if (node.type === 'Identifier') {
            const idNodeName = node.name;
            this.addMemberExpression(idNodeName, state, node);
            innerState.prefix = `${node.name}.`;
        }
    }

    addMemberExpression(name, state, node) {
        if (!Object.hasOwnProperty.call(state, name)) {
            state[name] = [];
        }
        state[name].push(node);
        const { start, end } = node.loc;
        this.membersMap[`${start.line}.${start.column}:${end.line}.${end.column}`] = state[name];
    }

    findThen(node, states, checks) {
        if (!node) {
            return;
        }
        states = checks.map((check, index) => {
            try {
                const state = states[index];
                const checkPassed = check(node, state, check);
                return checkPassed;
            } catch (e) {
                return false;
            }

        });

        checks = checks.filter((check, index) => {
            return !!states[index];
        });

        states = states.filter(_ => !!_);

        if (!states.length) {
            return;
        }
        if (
            node.type === 'Identifier' ||
            node.type === 'StringLiteral' ||
            node.type === 'NumericLiteral' ||
            node.type === 'NullLiteral' ||
            node.type === 'RegExpLiteral' ||
            node.type === 'BreakStatement' ||
            node.type === 'BooleanLiteral' ||
            node.type === 'ContinueStatement'
        ) {
            return;
        }
        if (node.type === 'MemberExpression') {
            this.findThen(node.object, states, checks);
            this.findThen(node.property, states, checks);
        } else if (node.type === 'ExpressionStatement') {
            this.findThen(node.expression, states, checks);
        } else if (node.type === 'CallExpression') {
            this.findThen(node.callee, states, checks);
            node.arguments.forEach(_ => {
                this.findThen(_, states, checks);
            });
            if (this.methodsMap[node.callee.name]) {
                this.findThen(this.methodsMap[node.callee.name]?.init?.body, states, checks);
            }
        } else if (node.type === 'RestElement') {
            this.findThen(node.argument, states, checks);
        } else if (node.type === 'AssignmentPattern') {
            this.findThen(node.left, states, checks);
            this.findThen(node.right, states, checks);
        } else if (node.type === 'AwaitExpression') {
            this.findThen(node.argument, states, checks);
        } else if (node.type === 'WhileStatement') {
            this.findThen(node.test, states, checks);
            this.findThen(node.body, states, checks);
        } else if (node.type === 'IfStatement') {
            this.findThen(node.consequent, states, checks);
            this.findThen(node.test, states, checks);
            if (node.alternate) {
                this.findThen(node.alternate, states, checks);
            }
        } else if (node.type === 'BlockStatement' && node.body) {
            node.body.forEach(_ => {
                this.findThen(_, states, checks);
            });
        } else if (node.type === 'Program' && node.body) {
            node.body.forEach(_ => {
                this.findThen(_, states, checks);
            });
        } else if (node.type === 'FunctionDeclaration') {
            node.params.forEach(el => {
                this.findThen(el, states, checks);
            });
            this.findThen(node.body, states, checks);
        } else if (node.type === 'SwitchStatement') {
            this.findThen(node.discriminant, states, checks);
            node.cases.forEach(_ => {
                this.findThen(_, states, checks);
            });
        } else if (node.type === 'LogicalExpression') {
            this.findThen(node.left, states, checks);
            this.findThen(node.right, states, checks);
        } else if (node.type === 'SwitchCase') {
            if (node.test) {
                this.findThen(node.test, states, checks);
            }
            node.consequent.forEach(_ => {
                this.findThen(_, states, checks);
            });
        } else if (node.type === 'VariableDeclaration') {
            node.declarations.forEach(_ => {
                this.findThen(_, states, checks);
            });
        } else if (node.type === 'VariableDeclarator') {
            this.findThen(node.id, states, checks);
            this.findThen(node.init, states, checks);
        } else if (node.type === 'ObjectPattern') {
            node.properties.forEach(_ => {
                this.findThen(_, states, checks);
            });
        } else if (node.type === 'ObjectProperty') {
            this.findThen(node.key, states, checks);
            this.findThen(node.value, states, checks);
        } else if (node.type === 'ReturnStatement') {
            if (node.argument) {
                this.findThen(node.argument, states, checks);
            }
        } else if (node.type === 'AssignmentExpression') {
            this.findThen(node.left, states, checks);
            this.findThen(node.right, states, checks);
        } else if (node.type === 'ArrowFunctionExpression') {
            node.params.forEach(el => {
                this.findThen(el, states, checks);
            });
            this.findThen(node.body, states, checks);
        } else if (node.type === 'FunctionExpression') {
            node.params.forEach(el => {
                this.findThen(el, states, checks);
            });
            this.findThen(node.body, states, checks);
        } else if (node.type === 'UpdateExpression') {
            this.findThen(node.argument, states, checks);
        } else if (node.type === 'ArrayPattern') {
            node.elements.forEach(el => {
                this.findThen(el, states, checks);
            });
        } else if (node.type === 'ThrowStatement') {
            this.findThen(node.argument, states, checks);
        } else if (node.type === 'ParenthesizedExpression') {
            this.findThen(node.expression, states, checks);
        } else if (node.type === 'UnaryExpression') {
            this.findThen(node.argument, states, checks);
        } else if (node.type === 'ForStatement') {
            this.findThen(node.body, states, checks);
        } else if (node.type === 'ForInStatement') {
            this.findThen(node.body, states, checks);
        } else if (node.type === 'BinaryExpression') {
            this.findThen(node.left, states, checks);
            this.findThen(node.right, states, checks);
        } else if (node.type === 'TemplateLiteral') {
            if (node.expressions) {
                node.expressions.forEach(_ => {
                    this.findThen(_, states, checks);
                });
            }
        } else if (node.type === 'DoWhileStatement') {
            if (node.body) {
                this.findThen(node.body, states, checks);
            }
            if (node.test) {
                this.findThen(node.test, states, checks);
            }
        } else if (node.type === 'ForOfStatement') {
            if (node.body) {
                this.findThen(node.body, states, checks);
            }
            if (node.left) {
                this.findThen(node.left, states, checks);
            }
            if (node.right) {
                this.findThen(node.right, states, checks);
            }
        } else if (node.type === 'ArrayExpression') {
            if (node.elements) {
                node.elements.forEach(_ => {
                    this.findThen(_, states, checks);
                });
            }
        } else if (node.type === 'NewExpression') {
            if (node.arguments) {
                node.arguments.forEach(_ => {
                    this.findThen(_, states, checks);
                });
            }
        } else if (node.type === 'ObjectExpression') {
            if (node.properties) {
                node.properties.forEach(_ => {
                    this.findThen(_.value, states, checks);
                });
            }
        } else if (node.type === 'SpreadElement') {
            if (node.argument) {
                this.findThen(node.argument, states, checks);
            }
        } else if (node.type === 'TryStatement') {
            this.findThen(node.block, states, checks);
            this.findThen(node.handler, states, checks);
        } else if (node.type === 'CatchClause') {
            this.findThen(node.body, states, checks);
            this.findThen(node.param, states, checks);
        } else if (node.type === 'OptionalMemberExpression') {
            this.findThen(node.object, states, checks);
            this.findThen(node.property, states, checks);
        } else if (node.type === 'ConditionalExpression') {
            if (node.alternate) {
                this.findThen(node.alternate, states, checks);
            }
            if (node.consequent) {
                this.findThen(node.consequent, states, checks);
            }
            this.findThen(node.test, states, checks);
        } else {
            // console.warn('Unsupported node', node);
        }
    }

    parseStrings(strings, isString) {
        strings.forEach(str => {
            this.parseString(str, isString);
        });
    }

    parseString(str, isString) {
        const bracketStack = [];
        const lookupBrackets = [['(', ')']];
        const validPairs = [];

        if (str.type === 'StringLiteral') {
            str.value.split('').forEach((letter, index) => {
                const foundGroup = lookupBrackets.find(bracketGroup => {
                    return bracketGroup.includes(letter);
                });

                if (!foundGroup) {
                    return;
                }

                const lastBracket = bracketStack.length ? bracketStack[bracketStack.length - 1].letter : null;

                if (lastBracket) {
                    if (foundGroup.indexOf(lastBracket) === 0 && foundGroup.indexOf(letter) === 1) {
                        validPairs.push({
                            start: bracketStack.pop(),
                            end: { letter, index, lineMod: 0, lineOffset: 0 },
                            startNode: str,
                            endNode: str,
                            type: 'string'
                        });
                    } else {
                        bracketStack.push({ letter, index, lineMod: 0, lineOffset: 0, part: str, type: 'string' });
                    }
                } else {
                    bracketStack.push({ letter, index, lineMod: 0, lineOffset: 0, part: str, type: 'string' });
                }
            });
        } else if (str.type === 'TemplateLiteral') {
            str.quasis.forEach(part => {
                let lineMod = 0;
                let lineOffset = 0;
                part.value.raw.split('').forEach((letter, index) => {

                    const foundGroup = lookupBrackets.find(bracketGroup => {
                        return bracketGroup.includes(letter);
                    });

                    if (letter === '\n') {
                        lineOffset = 0;
                        lineMod = lineMod + 1;
                    } else {
                        lineOffset++;
                    }

                    if (!foundGroup) {
                        return;
                    }

                    const lastBracket = bracketStack.length ? bracketStack[bracketStack.length - 1].letter : null;

                    if (lastBracket) {
                        if (foundGroup.indexOf(lastBracket) === 0 && foundGroup.indexOf(letter) === 1) {
                            const prev = bracketStack.pop();
                            validPairs.push({
                                start: prev,
                                end: { letter, index: index - 1, lineMod, lineOffset },
                                startNode: prev.part,
                                endNode: part,
                                type: 'string'
                            });
                        } else {
                            bracketStack.push({ letter, index: index - 1, part, lineMod, lineOffset, type: 'string' });
                        }
                    } else {
                        bracketStack.push({ letter, index: index - 1, part, lineMod, lineOffset, type: 'string' });
                    }
                });
            });
        }

        const invalidBrackets = bracketStack;

        validPairs.forEach((pair) => {
            this.addBracketPair(
                pair.end.lineMod ? pair.endNode.loc.start.line + pair.end.lineMod : pair.endNode.loc.start.line,
                pair.end.lineMod ? pair.end.lineOffset - 1 : pair.endNode.loc.start.column + pair.end.index + 1,
                pair.start.lineMod ? pair.startNode.loc.start.line + pair.start.lineMod : pair.startNode.loc.start.line,
                pair.start.lineMod ? pair.start.lineOffset - 1 : pair.startNode.loc.start.column + pair.start.index + 1,
            );

            if (pair.type === 'string') {
                this.addStringBracketPair(
                    pair.end.lineMod ? pair.endNode.loc.start.line + pair.end.lineMod : pair.endNode.loc.start.line,
                    pair.end.lineMod ? pair.end.lineOffset - 1 : pair.endNode.loc.start.column + pair.end.index + 1,
                    pair.start.lineMod ? pair.startNode.loc.start.line + pair.start.lineMod : pair.startNode.loc.start.line,
                    pair.start.lineMod ? pair.start.lineOffset - 1 : pair.startNode.loc.start.column + pair.start.index + 1,
                );
            }
        });

        invalidBrackets.forEach(bracket => {
            const line = bracket.lineMod
                ? bracket.part.loc.start.line + bracket.lineMod
                : bracket.part.loc.start.line;
            const column = bracket.lineMod
                ? bracket.lineOffset - 1
                : bracket.part.loc.start.column + bracket.index + 1;
            this.invalidBrackets.push({
                line,
                column,
                node: bracket.part,
                letter: bracket.letter,
                isString,
            });
        });
    }

    createError(e, str, fileName) {
        const expected = [...new Set(e.expected
            .filter(exp => exp.type === 'literal' && !!exp.text)
            .map(exp => exp.text)).values()];
        if (e.found === null) {
            this.syntaxErrors.push({
                file: fileName,
                line: str.loc.start.line,
                reason: `Unexpected end of pattern`,
                evidence: e,
                character: str.loc.start.column + 1 + e.location.start.column,
            });
        } else if (e.found === '(' && expected.includes('$(')) {
            // this.syntaxErrors.push({
            //     file: fileName,
            //     line: str.loc.start.line,
            //     reason: `Invalid pattern format at col ${str.loc.start.column + 1 + e.location.start.column}. Example "(alternative 1|alternative 2)" or "(optional alternative 1|optional alternative 2|)"`,
            //     evidence: e,
            //     character: str.loc.start.column + 1 + e.location.start.column,
            // });
        } else if (expected.join('').includes('*~DATE')) {
            this.syntaxErrors.push({
                file: fileName,
                line: str.loc.start.line,
                reason: `Expected slot name but found "${e.found}"`,
                evidence: e,
                character: str.loc.start.column + 1 + e.location.start.column,
            });
        } else {
            let errMsgInner = '';
            expected.forEach((exp, index) => {
                if (index + 1 === expected.length && expected.length > 1) {
                    errMsgInner += 'or ';
                } else if (index) {
                    errMsgInner += ', ';
                }
                errMsgInner += `"${exp}"`;
            });

            if (errMsgInner) {
                // this.syntaxErrors.push({
                //     file: fileName,
                //     line: str.loc.start.line,
                //     reason: `Expected ${errMsgInner}, but found "${e.found}"`,
                //     evidence: e,
                //     character: str.loc.start.column + 1 + e.location.start.column,
                // });
            } else {
                this.syntaxErrors.push({
                    file: fileName,
                    line: str.loc.start.line,
                    reason: `Unexpected syntax error at col ${str.loc.start.column + 1 + e.location.start.column}`,
                    evidence: e,
                    character: str.loc.start.column + 1 + e.location.start.column,
                });
            }
        }
    }

    parseData(astData, fileName, onlyErrors, keepData) {
        contextIndex = 1;
        try {
            if (!astData && astData !== '') {
                return;
            }

            this.patternStrings.clear();
            this.playStrings.clear();
            this.invalidBrackets = [];

            if (!keepData === true) {
                if (!onlyErrors) {
                    this.reset();
                }
            }

            this.script = astData;
            this.fileName = fileName;

            this.syntaxErrors = this.syntaxErrors.concat(validateWithJsHint(astData).map(err => {
                return {
                    file: fileName,
                    line: err.line,
                    reason: err.reason,
                    evidence: err.evidence,
                    character: err.character,
                    isWarning: err.isWarning,
                };
            }));

            const data = babelParser.parse(astData, { createParenthesizedExpressions: true, sourceType: 'module' });

            this.parseScript(data.program, onlyErrors);

            if (!onlyErrors) {
                this.context.forEach(ctx => {
                    ctx.intent = ctx.actions.filter(action => action.type === 'intent');
                    ctx.follow = ctx.actions.filter(action => action.type === 'follow');
                    ctx.question = ctx.actions.filter(action => action.type === 'question');
                    ctx.fallback = ctx.actions.filter(action => action.type === 'fallback');
                });

                this.parseStrings(this.strings, true);

                this.visualList = Object.keys(this.visualMap).map(_ => this.visualMap[_]);
                this.projectVariablesList = Object.keys(this.projectVariableMap).map(_ => this.projectVariableMap[_]);
                this.visualRelationships = [...Object.values(this.visualRelationshipsMap)];
            }

            this.parseStrings([...this.patternStrings.values(), ...this.playStrings.values()]);

            [...this.patternStrings.values()].forEach(str => {
                const value = str.type === 'TemplateLiteral'
                    ? str.quasis.map(_ => _.value.raw.trim()).join(' variable ')
                    : str.value.trim();
                try {
                    PegParser.parse(value);
                } catch (e) {
                    this.createError(e, str, fileName);
                }
            });
            [...this.playStrings.values()].forEach(str => {
                const value = str.type === 'TemplateLiteral'
                    ? str.quasis.map(_ => _.value.raw.trim()).join(' variable ')
                    : str.value.trim();
                try {
                    PegPlayParser.parse(value);
                } catch (e) {
                    this.createError(e, str, fileName);
                }
            });

            this.syntaxErrors = this.syntaxErrors.concat(this.invalidBrackets.filter(bracket => !bracket.isString).map(bracket => {
                return {
                    file: fileName,
                    line: bracket.line,
                    reason: `Missing bracket pair for "${bracket.letter}" at col ${bracket.column}`,
                    character: bracket.column,
                };
            }));
            this.error = null;
        } catch (e) {
            if (e.name === 'SyntaxError') {
                const { message } = e;
                const messageParts = message.split('(');
                const [line, column] = messageParts[1].replace(')', '').split(':');
                const cleanMsg = messageParts[0];
                this.syntaxErrors.push({
                    file: fileName,
                    line: parseInt(line),
                    reason: `${cleanMsg} at col ${column}`,
                    character: parseInt(column),
                });
            }
            this.error = e;
        }
    }
}
